import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TennisBall, Eye } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain flex">
      {/* Hero */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1646651105426-e8c8ee9badde?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Padel"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C] via-[#0A0A0C]/70 to-transparent" />
        <div className="absolute bottom-16 left-16 z-10">
          <div className="flex items-center gap-3 mb-6">
            <TennisBall size={40} weight="fill" className="text-padel-green" />
            <span className="font-heading text-3xl font-extrabold uppercase tracking-widest">Padel Live</span>
          </div>
          <h1 className="font-heading text-6xl font-extrabold uppercase leading-none tracking-tight max-w-md">
            Marcador en <span className="text-padel-blue text-glow-blue">tiempo real</span> para tu club
          </h1>
        </div>
      </div>

      {/* Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="flex items-center gap-2 mb-2 lg:hidden">
            <TennisBall size={32} weight="fill" className="text-padel-green" />
            <span className="font-heading text-2xl font-extrabold uppercase tracking-widest">Padel Live</span>
          </div>
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-padel-blue mb-2">Acceso al panel</p>
          <h2 className="font-heading text-4xl font-extrabold uppercase mb-8">
            {mode === "login" ? "Iniciar sesión" : "Crear cuenta"}
          </h2>

          <form onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <Input
                data-testid="register-name-input"
                placeholder="Nombre"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="bg-[#0A0A0C] border-0 border-b-2 border-white/20 rounded-none focus-visible:ring-0 focus:border-padel-blue h-12"
              />
            )}
            <Input
              data-testid="login-email-input"
              type="email"
              placeholder="Correo electrónico"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-[#0A0A0C] border-0 border-b-2 border-white/20 rounded-none focus-visible:ring-0 focus:border-padel-blue h-12"
            />
            <Input
              data-testid="login-password-input"
              type="password"
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-[#0A0A0C] border-0 border-b-2 border-white/20 rounded-none focus-visible:ring-0 focus:border-padel-blue h-12"
            />

            {error && <p data-testid="login-error" className="text-padel-danger text-sm font-medium">{error}</p>}

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={busy}
              className="w-full h-12 bg-padel-blue text-black font-heading text-lg uppercase tracking-widest rounded-none hover:bg-padel-green transition-colors"
            >
              {busy ? "…" : mode === "login" ? "Entrar" : "Registrarme"}
            </Button>
          </form>

          <button
            data-testid="toggle-auth-mode-btn"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="mt-6 text-sm text-muted-foreground hover:text-padel-blue transition-colors"
          >
            {mode === "login" ? "¿No tienes cuenta? Crear una" : "¿Ya tienes cuenta? Inicia sesión"}
          </button>

          <a
            href="/display"
            data-testid="view-display-link"
            className="mt-8 flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground hover:text-padel-green transition-colors"
          >
            <Eye size={16} /> Ver pantalla pública
          </a>
        </motion.div>
      </div>
    </div>
  );
}
